<template>
<v-container fluid fill-height>    
    <v-layout v-if="loading" align-center justify-center>
        <v-progress-circular indeterminate></v-progress-circular>
    </v-layout>
    <v-layout v-else>
        <v-flex xs12>
            <v-card>
                <v-toolbar color="primary" class="white--text">
                    <v-toolbar-title>Trap Configuration</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon dark large>bug_report</v-icon>
                </v-toolbar>
                <v-container class="pt-1" fluid grid-list-md>
                    <div v-if="filterVisible">
                        <v-layout row>
                            <v-flex xs12>
                                <v-alert :value="error != null" outline type="error">
                                    {{error}}
                                </v-alert>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap>                            
                            <v-flex xs4>
                                <v-autocomplete                                
                                    label="Category"
                                    item-value="ProductCategoryID"
                                    item-text="ProductCategoryName"
                                    :items="availableCategories"
                                    v-model="selectedCategoryIDs"                                    
                                    multiple
                                    hide-details
                                    clearable
                                    dense
                                    outline
                                    >
                                </v-autocomplete>
                            </v-flex>
                            <v-flex xs4>
                                <v-text-field class="filter-box" v-model="search" append-icon="search" label="Filter" single-line hide-details outline dense></v-text-field>
                            </v-flex>
                            <v-flex xs1>
                                <v-checkbox class="active-checkbox" label="Active Only" v-model="activeOnly" hide-details></v-checkbox>
                            </v-flex>
                            <v-spacer></v-spacer>
                            <v-flex xs1>
                                <v-btn color="primary" block @click="showEditAddTraps({ IsActive: true })">
                                    Add
                                </v-btn>
                            </v-flex>
                        </v-layout>
                        <v-layout pt-3>
                            <v-flex xs12>
                                <hr />
                            </v-flex>                         
                        </v-layout>
                    </div>

                    <v-layout row align-center :class="`flex-grow-0 d-flex`">
                        <v-flex grow class="subheading font-weight-bold info--text px-1">
                            <v-btn small block flat @click="toggleNavigationAndFilters">
                                <span v-if="filterVisible">Hide Controls</span>
                                <span v-else>Show Controls</span>
                            </v-btn>
                        </v-flex>
                    </v-layout>
                    <v-layout pt-3>
                        <v-flex xs12>
                            <v-data-table :headers="headers" :items="filteredTraps" :search="search" :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" must-sort>
                                <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                                <template slot="items" slot-scope="props">
                                    <td>{{props.item.PlantName}}</td>
                                    <td>{{props.item.DivisionName}}</td>
                                    <td>{{props.item.LocationName}}</td>
                                    <td>{{props.item.TrapIdentifier}}</td>
                                    <td>{{props.item.TrapType}}</td>
                                    <td>{{props.item.TrapZoneName}}</td>
                                    <td>{{ props.item.DatePlaced | formatDate }}</td>
                                    <td>{{props.item.IsActive ? 'Active' : 'Inactive'}}</td>
                                    <td>
                                        <v-btn color="primary" block @click="showEditAddTraps(props.item)">
                                            Edit
                                        </v-btn>
                                    </td>
                                </template>
                            </v-data-table>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card>
        </v-flex>
    </v-layout>
    <v-dialog v-model="showAddEditTrapDialog" fullscreen hide-overlay transition="dialog-bottom-transition" @keydown.esc="cancelEdit">
        <v-card>
            <v-toolbar dark color="primary">
                <v-btn icon dark :disabled="saving" @click="cancelEdit">
                    <v-icon large>close</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <v-toolbar-title v-if="editing">Edit Trap</v-toolbar-title>
                <v-toolbar-title v-else>Add New Trap</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                </v-toolbar-items>
            </v-toolbar>
            <v-form ref="editForm" v-model="editValid">
                <v-layout class="dialog-content">
                    <v-flex xs12 md6 offset-md3 mt-4>
                        <v-layout wrap>
                            <v-flex xs12>
                                <v-text-field v-model="editItem.TrapIdentifier" :rules="[rules.required, rules.maxTwoHundredCharacters]" counter label="Identifier (ID)">
                                </v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field v-model="editItem.TrapType" :rules="[rules.maxTwoHundredCharacters]" counter label="Type">
                                </v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-select v-model="editItem.PlantID" :items="plants" item-text="PlantName" item-value="PlantID" :rules="[rules.requiredObject]" @input="clearDivision" label="Plant">
                                </v-select>
                            </v-flex>
                            <v-flex xs12>
                                <v-select v-model="editItem.DivisionID" :items="divisions" item-text="DivisionName" item-value="DivisionID" :rules="[rules.requiredObject]" @input="clearLocation" label="Division">
                                </v-select>
                            </v-flex>
                            <v-flex xs12>
                                <v-select v-model="editItem.LocationID" :items="locations" item-text="LocationName" item-value="LocationID" :rules="[rules.requiredObject]" label="Location">
                                </v-select>
                            </v-flex>
                            <v-flex xs12>
                                <v-autocomplete
                                    label="Product Categories"
                                    :items="availableCategories"
                                    item-value="ProductCategoryID"
                                    item-text="ProductCategoryName"
                                    v-model="editItem.ProductCategories"
                                    :rules="[rules.required]"                                     
                                    chips 
                                    multiple                                    
                                    placeholder="Categories"
                                    >
                                </v-autocomplete>
                            </v-flex>
                            <v-flex xs12>
                                <v-select v-model="editItem.TrapZoneID" :items="trapZones" item-text="Name" item-value="TrapZoneID" label="Zone">
                                </v-select>
                            </v-flex>
                            <v-flex xs12>
                                <v-autocomplete
                                    label="Pheromones"
                                    :items="pheromones"
                                    item-value="PheromoneID"
                                    item-text="PheromoneName"
                                    v-model="editItem.Pheromones"                                     
                                    chips 
                                    multiple
                                    clearable
                                    placeholder="Pheromones"
                                    >
                                </v-autocomplete>
                            </v-flex>
                            <v-flex xs12>
                                <v-menu                                    
                                    v-model="dateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                    full-width
                                    min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field
                                            :value="editItem.DatePlaced | formatDate"
                                            label="Date Placed"
                                            prepend-icon="event"
                                            readonly v-on="on"
                                            append-icon="close"
                                            @click:append="clearDatePlaced"
                                            ></v-text-field>
                                    </template>
                                    <v-date-picker :value="formatDate(editItem.DatePlaced)" @input="updateDatePlaced" ></v-date-picker>                                                                    
                                </v-menu>                             
                            </v-flex>
                            <v-flex xs12>
                                <v-menu                                    
                                    v-model="expiryDateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                    full-width
                                    min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field
                                            :value="editItem.ExpiryDate | formatDate"
                                            label="Expiry Date"
                                            prepend-icon="event"
                                            readonly v-on="on"
                                            append-icon="close"
                                            @click:append="clearExpiryDate"
                                            ></v-text-field>
                                    </template>
                                    <v-date-picker :value="formatDate(editItem.ExpiryDate)" @input="updateExpiryDate" ></v-date-picker>                                                                    
                                </v-menu>                             
                            </v-flex>
                            <v-flex xs12 v-if="editItem.Latitude">
                                <v-text-field v-model="editItem.Latitude" disabled label="Latitude">
                                </v-text-field>
                            </v-flex>
                            <v-flex xs12 v-if="editItem.Longitude">
                                <v-text-field v-model="editItem.Longitude" disabled label="Longitude">
                                </v-text-field>
                            </v-flex>
                            <v-flex>
                                <v-checkbox v-model="editItem.IsActive" label="Active"></v-checkbox>
                            </v-flex>
                        </v-layout>
                        <v-layout wrap class="dialog-action-buttons">
                            <v-flex xs12 sm6>
                                <v-btn block color="success" :disabled="!editValid" :loading="saving" @click="save(editItem)">Save</v-btn>
                            </v-flex>
                            <v-flex xs12 sm6>
                                <v-btn block color="error" :disabled="saving" @click="cancelEdit">Cancel</v-btn>
                            </v-flex>
                            <v-flex xs12>
                                <v-alert :value="error != null" outline type="error">
                                    {{error}}
                                </v-alert>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-form>
        </v-card>
    </v-dialog>
</v-container>
</template>

<script>
import api from '@/api/trapApi'
import validationRules from "@/helpers/validationrules";
import UtilService from '@/services/utilService.js';
import productCategoryApi from "@/api/productCategoryApi";

export default {
    data() {
        return {
            traps: [],
            locationModels: [],
            trapZones: [],
            availableCategories: [],
            pheromones: [],
            selectedCategoryIDs: [],
            editItem: { PlantID: null, DivisionID: null, LocationID: null },
            loading: false,
            search: '',
            activeOnly: true,
            showAddEditTrapDialog: false,
            rules: validationRules.rules,
            editing: false,
            editValid: true,
            saving: false,
            error: null,
            dateMenu: false,
            expiryDateMenu: false,
            rowsPerPageItems: [10, 25, 50, 100],
            pagination: {
                page: 1,
                rowsPerPage: 25,
                descending: false
            },
            headers: [{
                    text: "Plant",
                    value: "PlantName",
                    align: 'left',
                },
                {
                    text: "Division",
                    value: "DivisionName",
                    align: 'left'
                },
                {
                    text: "Location",
                    value: "LocationName",
                    align: 'left'
                },
                {
                    text: "Trap ID",
                    value: "TrapIdentifier",
                    align: 'left'
                },
                {
                    text: "Trap Type",
                    value: "TrapTypeName",
                    align: 'left'
                },
                {
                    text: "Zone",
                    value: "TrapZoneName",
                    align: 'left'
                },
                {
                    text: "Placed",
                    value: "DatePlaced",
                    align: 'left'
                },
                {
                    text: "Active",
                    value: "IsActive",
                    align: 'left'
                },
                {
                    text: "Edit",
                    value: "",
                    align: "left",
                    sortable: false
                }
            ],
            filterVisible: true,
        }
    },
    computed: {
        filteredTraps() {
            let filtered = this.traps.filter(c => c.IsActive || !this.activeOnly);

            if (this.selectedCategoryIDs.length > 0) {
                filtered = filtered.filter(c => c.ProductCategories.some(pc => this.selectedCategoryIDs.some(s => s == pc)));
            }

            return filtered;
        },
        plants() {
            // Distinct Plants
            const map = new Map();
            const result = [];
            for(const location of this.locationModels) {
                if(!map.has(location.Division.Plant.PlantID)) {
                    map.set(location.Division.Plant.PlantID);
                    result.push(location.Division.Plant);
                }
            }

            return result;
        },
        divisions() {
             // Distinct Divisions                
            const map = new Map();
            const result = [];
            for(const location of this.locationModels) {
                if(!map.has(location.Division.DivisionID) && location.Division.Plant.PlantID === this.editItem.PlantID) {
                    map.set(location.Division.DivisionID);
                    result.push(location.Division);
                }
            }

            return result;
        },
        locations() {
            return this.locationModels.filter(l => l.Division.DivisionID === this.editItem.DivisionID);
        }
    },
    methods: {
        async loadLookups() {
            this.loading = true;
            this.error = null;

            try {
                this.locationModels = await api.getLocations();
                this.trapZones = await api.getTrapZones();
                this.pheromones = await api.getPheromones();
                
                const productCategories = await productCategoryApi.getProductCategories();

                this.availableCategories = productCategories.map(x => {
                    return {
                        ProductCategoryID: x.ID,
                        ProductCategoryName: x.Name
                    };
                });
            } catch(err) {
                this.error = err;
            }

            this.loading = false;
        },
        async loadTraps() {
            this.loading = true;
            this.error = null;

            try {                
                this.traps = await api.getTraps();
            } catch(err) {
                this.error = err;
            }

            this.loading = false;
        },
        async showEditAddTraps(item) {            
            Object.assign(this.editItem, item);
            this.showAddEditTrapDialog = true;            
        },
        async save(item) {
            this.saving = true;
            this.error = null;

            try {
                let response = {};

                if(item.TrapID) {
                    response = await api.updateTrap(item);
                } else {
                    response = await api.addTrap(item);
                }

                if(!response.Success) {
                    this.error = response.ErrorMessages.join(', ');
                    return;
                }

                await this.loadTraps();
            } catch(err) {
                this.error = err;
                return;
            } finally {
                this.saving = false;
            }
            
            this.editItem = {};
            this.$refs.editForm.reset();
            this.showAddEditTrapDialog = false;
        },
        async cancelEdit() {
            this.editItem = {};
            this.showAddEditTrapDialog = false;
            this.error = null;
            this.$refs.editForm.reset();
        },
        clearDatePlaced() {
            this.dateMenu = true;
            this.$set(this.editItem, 'DatePlaced', null);
            this.dateMenu = false;
        },
        updateDatePlaced(v) {
            this.$set(this.editItem, 'DatePlaced', v);
            this.dateMenu = false;
        },
        clearExpiryDate() {
            this.expiryDateMenu = true;
            this.$set(this.editItem, 'ExpiryDate', null);
            this.expiryDateMenu = false;
        },
        updateExpiryDate(v) {
            this.$set(this.editItem, 'ExpiryDate', v);
            this.expiryDateMenu = false;
        },
        formatDate(v) {
            if (v) {
                return UtilService.formatDateYMD(v);
            }
            
            return null;
        },
        clearDivision() {
            this.editItem.DivisionID = null;
            this.editItem.LocationID = null;
        },
        clearLocation() {
            this.editItem.LocationID = null;
        },
        toggleNavigationAndFilters() {
            this.filterVisible = !this.filterVisible;
        }
    },
    async created() {
        await this.loadLookups();
        await this.loadTraps();
    }
}
</script>
<style scoped>
.filter-box >>> .v-input__slot {
    padding-bottom: 12px !important;
}
</style>